
import React, { useState, useEffect, useCallback } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faTrash,
  faSearch,
  faSortAmountDown,
  faFileExport,
  faExclamationTriangle,
  faSync,
  faToggleOn,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import apiHelper from "../ApiHelpar/apiHelper";
import {
  GET_ALL_HOSPITAL_BY_STATUS,
  STATUSCHANGEBYHOSPITALTD,
} from "../visitConstant/ApiConstant";
import useDebounce from "../useDebounce/useDebounce";
import swal from "sweetalert";

const stylebg = {
  backgroundColor: "#f8f9fa",
  paddingTop: 18,
  paddingBottom: 18,
  fontSize: 14,
  color: "#6c757d",
  whiteSpace: "nowrap",
};

const HospitalList = ({
  expandedView,
  setDataEdit,
  // hospitalId,
  // BaranchId,
  Role,
  RefreshApi,
}) => {
  // State management
  const [hospitalList, setHospitalList] = useState([]);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [dataListActive, setDataListActive] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Use debounce to prevent excessive API calls during search
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Memoized fetch function to avoid recreating on every render
  const fetchHospitals = useCallback(
    async (page = currentPage) => {
      setLoading(true);
      setError(null);

      try {
        const offset = (page - 1) * itemsPerPage;
        const response = await apiHelper(GET_ALL_HOSPITAL_BY_STATUS, {
          name: debouncedSearchTerm,
          offset,
          pageSize: itemsPerPage,
          active: dataListActive,
          // sortField,
          // sortDirection: sortDirection.toUpperCase(),
        });

        if (response?.respInfo?.code === "0") {
          setHospitalList(response.respData?.respMsg.content || []);
          setTotalItems(response.respData?.respMsg.totalElements || 0);
        } else {
          throw new Error(
            response?.respInfo?.message || "Failed to fetch hospitals"
          );
        }
      } catch (error) {
        console.error("Error fetching hospitals:", error);
        setError(error.message || "An unexpected error occurred");
        setHospitalList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [
      currentPage,
      itemsPerPage,
      debouncedSearchTerm,
      dataListActive,
      sortField,
      sortDirection,
    ]
  );

  // Reset to first page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    dataListActive,
    sortField,
    sortDirection,
    itemsPerPage,
  ]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchHospitals(currentPage);
  }, [currentPage, RefreshApi]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Handle hospital status change (activate/deactivate)
  const handleStatusChange = async (hospital) => {
    const action = hospital.active ? "deactivate" : "activate";

    swal({
      title: "Are you sure?",
      text: `Do you want to ${action} this hospital?`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: hospital.active,
    }).then(async (confirmed) => {
      if (confirmed) {
        try {
          const response = await apiHelper(STATUSCHANGEBYHOSPITALTD, {
            id: hospital.id,
            active: !hospital.active,
          });

          if (response?.respInfo?.code === "0") {
            swal("Success!", `Hospital ${action}d successfully.`, "success");
            fetchHospitals(); // Refresh the list
          } else {
            throw new Error(
              response?.respInfo?.message || `Failed to ${action} hospital`
            );
          }
        } catch (error) {
          swal(
            "Error!",
            error.message || `Failed to ${action} hospital.`,
            "error"
          );
        }
      }
    });
  };

  // Export data to CSV
  const exportToCSV = async () => {
    setExportLoading(true);

    try {
      // Fetch all records for export
      const response = await apiHelper(GET_ALL_HOSPITAL_BY_STATUS, {
        name: debouncedSearchTerm,
        offset: 0,
        pageSize: 1000, // Get a large batch, adjust as needed
        active: dataListActive,
        sortField,
        sortDirection: sortDirection.toUpperCase(),
      });

      if (response?.respInfo?.code === "0") {
        const dataToExport = response.respData?.respMsg.content || [];

        if (dataToExport.length === 0) {
          swal("No Data", "There are no records to export.", "info");
          return;
        }

        // Prepare CSV content
        const csvHeader =
          "ID,Name,Location,Status,Registration Date,Closing Date\n";
        const csvRows = dataToExport
          .map(
            (hospital) =>
              `${hospital.id},"${hospital.name.replace(
                /"/g,
                '""'
              )}","${hospital.location.replace(/"/g, '""')}",${
                hospital.active ? "Active" : "Inactive"
              },${moment(hospital.registrationDate).format(
                "YYYY-MM-DD"
              )},${moment(hospital.closingDate).format("YYYY-MM-DD")}`
          )
          .join("\n");

        const csvContent = csvHeader + csvRows;
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);

        // Create download link and trigger download
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `hospital_list_${moment().format("YYYYMMDD_HHmmss")}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        swal("Success!", "Export completed successfully.", "success");
      } else {
        throw new Error(response?.respInfo?.message || "Failed to export data");
      }
    } catch (error) {
      console.error("Export error:", error);
      swal(
        "Export Failed",
        error.message || "An error occurred during export.",
        "error"
      );
    } finally {
      setExportLoading(false);
    }
  };

  // Calculate pagination values
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const showingStart =
    totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const showingEnd = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate pagination items with ellipsis for large page counts
  const getPaginationItems = () => {
    const items = [];

    if (totalPages <= 7) {
      // Show all pages if there are 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        items.push(i);
      }
    } else {
      // Always include first page
      items.push(1);

      if (currentPage > 3) {
        items.push("...");
      }

      // Current page and neighbors
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        items.push(i);
      }

      if (currentPage < totalPages - 2) {
        items.push("...");
      }

      // Always include last page
      items.push(totalPages);
    }

    return items;
  };

  // Handle per page change
  const handlePerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
  };

  // Render loading state
  if (loading && hospitalList.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      {/* Header Section with Search and Export */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-3 px-3 mt-3">
        <div className="d-flex flex-column flex-sm-row gap-2 w-100">
          <div className="input-group">
            <span
              className={`input-group-text ${
                dataListActive ? "bg-white" : "bg-primary text-white"
              }`}
              onClick={() => setDataListActive(!dataListActive)}
              style={{ cursor: "pointer" }}
              title={
                dataListActive
                  ? "Showing active hospitals"
                  : "Showing inactive hospitals"
              }
            >
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search hospitals"
            />
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => setSearchTerm("")}
              disabled={!searchTerm}
            >
              Clear
            </button>
          </div>

          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={exportToCSV}
              disabled={loading || exportLoading || hospitalList.length === 0}
            >
              {exportLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Exporting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  Export
                </>
              )}
            </button>

            <button
              className="btn btn-outline-primary"
              onClick={() => fetchHospitals(currentPage)}
              disabled={loading}
              title="Refresh data"
            >
              <FontAwesomeIcon icon={faSync} spin={loading} />
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger mx-3" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
          <button
            type="button"
            className="btn-close float-end"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Table Section */}
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th scope="col" style={stylebg} className="rounded-start">
                #
              </th>
              <th
                scope="col"
                style={{ ...stylebg, cursor: "pointer" }}
                onClick={() => handleSort("name")}
              >
                <div className="d-flex align-items-center">
                  Name
                  {sortField === "name" && (
                    <FontAwesomeIcon
                      icon={faSortAmountDown}
                      className={`ms-2 ${
                        sortDirection === "desc" ? "rotate-180" : ""
                      }`}
                      style={{ fontSize: 12 }}
                    />
                  )}
                </div>
              </th>
              <th
                scope="col"
                style={{ ...stylebg, cursor: "pointer" }}
                onClick={() => handleSort("location")}
              >
                <div className="d-flex align-items-center">
                  Location
                  {sortField === "location" && (
                    <FontAwesomeIcon
                      icon={faSortAmountDown}
                      className={`ms-2 ${
                        sortDirection === "desc" ? "rotate-180" : ""
                      }`}
                      style={{ fontSize: 12 }}
                    />
                  )}
                </div>
              </th>
              <th scope="col" style={stylebg}>
                Status
              </th>
              <th
                scope="col"
                style={{ ...stylebg, cursor: "pointer" }}
                onClick={() => handleSort("registrationDate")}
              >
                <div className="d-flex align-items-center">
                  Reg. Date
                  {sortField === "registrationDate" && (
                    <FontAwesomeIcon
                      icon={faSortAmountDown}
                      className={`ms-2 ${
                        sortDirection === "desc" ? "rotate-180" : ""
                      }`}
                      style={{ fontSize: 12 }}
                    />
                  )}
                </div>
              </th>
              <th
                scope="col"
                style={{ ...stylebg, cursor: "pointer" }}
                onClick={() => handleSort("closingDate")}
              >
                <div className="d-flex align-items-center">
                  Closing Date
                  {sortField === "closingDate" && (
                    <FontAwesomeIcon
                      icon={faSortAmountDown}
                      className={`ms-2 ${
                        sortDirection === "desc" ? "rotate-180" : ""
                      }`}
                      style={{ fontSize: 12 }}
                    />
                  )}
                </div>
              </th>
              <th
                scope="col"
                style={stylebg}
                className="rounded-end text-center"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {hospitalList.length > 0 ? (
              hospitalList.map((hospital, index) => (
                <tr
                  key={hospital.id}
                  className="fw-medium"
                  style={{ fontSize: 14 }}
                >
                  <td>{showingStart + index}</td>
                  <td>{hospital.name}</td>
                  <td>{hospital.location}</td>
                  <td>
                    <span
                      className={`badge ${
                        hospital.active ? "bg-success" : "bg-danger"
                      }`}
                    >
                      {hospital.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    {hospital.registrationDate
                      ? moment(hospital.registrationDate).format("DD-MMM-YYYY")
                      : "N/A"}
                  </td>
                  <td>
                    {hospital.closingDate
                      ? moment(hospital.closingDate).format("DD-MMM-YYYY")
                      : "N/A"}
                  </td>
                  <td>
                    <div className="d-flex justify-content-center gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setDataEdit(hospital)}
                        title="Edit Hospital"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${
                          hospital.active ? "btn-danger" : "btn-success"
                        }`}
                        onClick={() => handleStatusChange(hospital)}
                        title={
                          hospital.active
                            ? "Deactivate Hospital"
                            : "Activate Hospital"
                        }
                      >
                        <FontAwesomeIcon
                          icon={hospital.active ? faTrash : faSync}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center py-5">
                  {loading ? (
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <div className="text-muted">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="me-2"
                        size="lg"
                      />
                      No hospitals found matching your criteria
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Section */}
      {hospitalList.length > 0 && (
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center px-3 mt-3 mb-3">
          <div className="text-muted mb-3 mb-md-0">
            Showing {showingStart} to {showingEnd} of {totalItems} entries
          </div>
          <nav aria-label="Hospital list pagination">
            <ul className="pagination pagination-sm mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  Previous
                </button>
              </li>

              {getPaginationItems().map((item, index) =>
                item === "..." ? (
                  <li key={`ellipsis-${index}`} className="page-item disabled">
                    <span className="page-link">...</span>
                  </li>
                ) : (
                  <li
                    key={item}
                    className={`page-item ${
                      currentPage === item ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(item)}
                    >
                      {item}
                    </button>
                  </li>
                )
              )}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  Next
                </button>
              </li>
              <li>
                <select
                  className="form-select form-select-sm"
                  value={itemsPerPage}
                  onChange={handlePerPageChange}
                  style={{ width: "auto" }}
                >
                  {[5, 10, 25, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size} per page
                    </option>
                  ))}
                </select>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default HospitalList;


